import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useRouteLoaderData } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  BrandDto,
  HostnameDto,
  HostnameType,
  TenantDto,
  TenantInvitationDto,
  TenantMemberDto,
  TenantUserRole,
} from '@/api/types';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import { useCurrentOperator } from '@/auth/AuthContext';
import { useSnackbar } from '@/components/Snackbar';
import { DEFAULT_LOCALE, normalizeLocale } from '@/i18n/constants';
import { useCopy } from '@/hooks/useCopy';
import { StatusChip } from '@/components/StatusChip';
import { HostUrl } from '@/components/HostUrl';
import { isClickableHostType } from '@/components/hostUrlRules';
import type { TenantWorkspaceLoaderData } from './TenantWorkspace';

const TYPE_ORDER: HostnameType[] = ['APP', 'LOGIN', 'AUTH', 'API', 'ASSET', 'BRAND'];
const PAGE_SIZE = 5;
const ADMIN_ROLE_ORDER: Record<TenantUserRole, number> = {
  OWNER: 0,
  SUPER_ADMIN: 1,
  ADMIN: 2,
  STAFF: 3,
  USER: 4,
  GUEST: 5,
};

type CopyFn = ReturnType<typeof useCopy>['t'];

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtDateTime(iso: string, locale: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(locale);
}

// SASADM-0144 — one canonical row per (type, app). Same rule as
// TenantHostnames.primaryHostname (`:184`), keyed per-app for APP rows so a tenant with two
// apps shows both. Post-SASADM-0143 `isPrimary` is derived and always wins; the `?? first`
// fallback is what keeps a not-yet-flagged app host visible until then.
function groupKey(h: HostnameDto): string {
  return h.hostnameType === 'APP' ? `APP:${h.appSlug ?? ''}` : h.hostnameType;
}

function buildUrlRows(hostnames: HostnameDto[]): HostnameDto[] {
  const groups = new Map<string, HostnameDto[]>();
  for (const h of hostnames) {
    const key = groupKey(h);
    const arr = groups.get(key) ?? [];
    arr.push(h);
    groups.set(key, arr);
  }
  // Non-null: every `rows` array here got its first element from the loop above that created it.
  const canonical = [...groups.values()].map((rows) => rows.find((r) => r.isPrimary) ?? rows[0]!);
  return canonical.sort(
    (a, b) =>
      TYPE_ORDER.indexOf(a.hostnameType) - TYPE_ORDER.indexOf(b.hostnameType) ||
      (a.hostnameType === 'APP' ? (a.appSlug ?? '').localeCompare(b.appSlug ?? '') : 0),
  );
}

function urlTypeLabel(h: HostnameDto, t: CopyFn): string {
  const typeLabel = t(`hostnames.types.${h.hostnameType}`, h.hostnameType);
  const name = h.appName ?? h.appSlug;
  return name ? `${typeLabel}: ${name}` : typeLabel;
}

function usePaged<T>(rows: T[] | null): {
  pageRows: T[] | null;
  page: number;
  pageCount: number;
  setPage: (page: number) => void;
} {
  const [page, setPage] = useState(1);
  const pageCount = rows ? Math.max(1, Math.ceil(rows.length / PAGE_SIZE)) : 1;
  const clampedPage = Math.min(page, pageCount);
  const pageRows = rows ? rows.slice((clampedPage - 1) * PAGE_SIZE, clampedPage * PAGE_SIZE) : null;
  return { pageRows, page: clampedPage, pageCount, setPage };
}

export function TenantOverview() {
  const data = useRouteLoaderData('tenant-workspace') as TenantWorkspaceLoaderData;
  const tenant = data?.tenant;
  const brand = data?.brand;
  // SASADM-0135 — the tenant's product, resolved the same way every tab does (no product field on
  // the loader data itself). null when the tenant has no product; this component then shows every
  // warning/section exactly as it does today, unchanged.
  const product = tenant?.productId ? data.products.find((p) => p.id === tenant.productId) : null;
  const isExternal = product?.authMode === 'EXTERNAL';
  const isSelfContained = (product?.appTopology ?? 'COMPOSED') === 'SELF_CONTAINED';
  const { t } = useCopy();
  const operator = useCurrentOperator();
  const [hostnames, setHostnames] = useState<HostnameDto[] | null>(null);
  const [members, setMembers] = useState<TenantMemberDto[] | null>(null);
  const [invitations, setInvitations] = useState<TenantInvitationDto[] | null>(null);
  // SASADM-0170 (SASATH-0387 §3A) — apps whose public app is turned off for this tenant. Loaded
  // tolerantly: any error (a non-super-admin's 403, a pre-0387 404) → nothing is marked.
  const [disabledAppIds, setDisabledAppIds] = useState<ReadonlySet<number>>(new Set());

  useEffect(() => {
    if (!tenant) return;
    let cancelled = false;
    void apiAdmin.appOverrides
      .list(tenant.id)
      .then(
        (rows) =>
          !cancelled &&
          setDisabledAppIds(new Set(rows.filter((r) => !r.enabled).map((r) => r.appId))),
      )
      .catch(() => !cancelled && setDisabledAppIds(new Set()));
    void apiAdmin.hostnames
      .list(tenant.id)
      .then((rows) => !cancelled && setHostnames(rows))
      .catch(() => setHostnames([]));
    void apiAdmin.members
      .list(tenant.id)
      .then((rows) => !cancelled && setMembers(rows))
      .catch(() => setMembers([]));
    // SASADM-0144 §4 — pending only; accepted/revoked/expired are history, not "invited".
    void apiAdmin.invitations
      .list(tenant.id, 'PENDING')
      .then((rows) => !cancelled && setInvitations(rows))
      .catch(() => setInvitations([]));
    return () => {
      cancelled = true;
    };
  }, [tenant]);

  if (!tenant) return null;

  const appHost = hostnames?.find((h) => h.hostnameType === 'APP');
  const urlRows = hostnames ? buildUrlRows(hostnames) : null;
  const admins = members
    ? [...members]
        .filter((m) => m.role !== 'USER')
        .sort(
          (a, b) =>
            ADMIN_ROLE_ORDER[a.role] - ADMIN_ROLE_ORDER[b.role] ||
            (a.name ?? a.email).localeCompare(b.name ?? b.email),
        )
    : null;

  return (
    <Box>
      {hostnames && hostnames.length > 0 && !appHost ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {isSelfContained
            ? t(
                'tenants.overview.warnings.noAppHostnameSelfContained',
                'This tenant has no APP hostname. A self-contained product is reachable only through its APP host — register one.',
              )
            : t('tenants.overview.warnings.noAppHostname', '')}
        </Alert>
      ) : null}
      {members && members.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('tenants.overview.warnings.noMembers', '')}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <SettingsCard tenant={tenant} brand={brand} isSelfContained={isSelfContained} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <UrlsCard
            rows={urlRows}
            hostnames={hostnames}
            tenantId={tenant.id}
            isSelfContained={isSelfContained}
            showSupportAction={Boolean(operator?.isSuperAdmin)}
            disabledAppIds={disabledAppIds}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <OwnersCard members={admins} tenantId={tenant.id} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <InvitedCard invitations={invitations} tenantId={tenant.id} />
        </Grid>
      </Grid>
      {isSelfContained || isExternal ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          {isSelfContained && isExternal
            ? t(
                'tenants.overview.hostnamesReduced.both',
                'This product is self-contained and externally authenticated, so it does not use platform brand, asset, login or auth hosts.',
              )
            : isSelfContained
              ? t(
                  'tenants.overview.hostnamesReduced.selfContained',
                  'This product is self-contained, so it does not use platform brand or asset hosts.',
                )
              : t(
                  'tenants.overview.hostnamesReduced.external',
                  'This product is externally authenticated, so it does not use platform login or auth hosts.',
                )}
        </Alert>
      ) : null}
    </Box>
  );
}

function FieldLabel({ label }: { label: string }) {
  return (
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
      {label}
    </Typography>
  );
}

// SASADM-0144 §1 — general settings, full width, at the top of the tab.
function SettingsCard({
  tenant,
  brand,
  isSelfContained,
}: {
  tenant: TenantDto;
  brand: BrandDto | null;
  isSelfContained: boolean;
}) {
  const { t } = useCopy();
  const brandValue = isSelfContained
    ? t('tenants.overview.brandingNotApplicable', 'Does not apply — self-contained')
    : brand
      ? brand.displayName
      : t('tenants.overview.inheritsBrand', 'Inherits product default');

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
        >
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            {t('tenants.overview.cards.settings', 'Settings')}
          </Typography>
          <Link component={RouterLink} to={`/tenants/${tenant.id}/settings`} variant="body2">
            {t('tenants.overview.hostnames.manage', 'Manage')}
          </Link>
        </Stack>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FieldLabel label={t('tenants.fields.name', 'Name')} />
            <Typography variant="body2">{tenant.name}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FieldLabel label={t('tenants.fields.slug', 'Slug')} />
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {tenant.slug}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FieldLabel label={t('tenants.fields.signupMode', 'Signup mode')} />
            <StatusChip kind="tenantSignupMode" value={tenant.signupMode} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FieldLabel label={t('tenants.fields.brand', 'Brand')} />
            <Typography variant="body2">{brandValue}</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

// SASADM-0144 §2 — the tenant's canonical URLs, full width, below Settings. Not a
// CardActionArea (it holds a table); a "Manage" link routes to the Whitelabel tab.
function UrlsCard({
  rows,
  hostnames,
  tenantId,
  isSelfContained,
  showSupportAction,
  disabledAppIds,
}: {
  rows: HostnameDto[] | null;
  hostnames: HostnameDto[] | null;
  tenantId: number;
  isSelfContained: boolean;
  showSupportAction: boolean;
  disabledAppIds: ReadonlySet<number>;
}) {
  const { t } = useCopy();

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
        >
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            {t('tenants.overview.cards.urls', 'URLs')}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            {showSupportAction ? (
              <SupportAccessAction tenantId={tenantId} hostnames={hostnames} />
            ) : null}
            <Link component={RouterLink} to={`/tenants/${tenantId}/whitelabel`} variant="body2">
              {t('tenants.overview.hostnames.manage', 'Manage')}
            </Link>
          </Stack>
        </Stack>

        {rows === null ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('common.loading', 'Loading')}
          </Typography>
        ) : rows.length === 0 ? (
          <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {isSelfContained
                ? t(
                    'hostnames.emptySelfContained',
                    'No hostnames yet. A self-contained product is reachable through a single URL you register — add a custom domain to get started.',
                  )
                : t('hostnames.empty', 'No hostnames yet. Register an APP host so users can log in.')}
            </Typography>
            {isSelfContained && (
              <Button
                component={RouterLink}
                to={`/tenants/${tenantId}/whitelabel`}
                variant="outlined"
                size="small"
              >
                {t('hostnames.addCustomDomain', 'Add custom domain')}
              </Button>
            )}
          </Stack>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('hostnames.type', 'Type')}</TableCell>
                <TableCell>{t('tenants.overview.hostnames.colUrl', 'URL')}</TableCell>
                <TableCell>{t('tenants.overview.hostnames.colDns', 'DNS')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((h) => (
                <TableRow key={h.id}>
                  <TableCell>
                    <Typography variant="body2">{urlTypeLabel(h, t)}</Typography>
                  </TableCell>
                  <TableCell>
                    <HostUrl
                      hostname={h.hostname}
                      clickable={isClickableHostType(h.hostnameType)}
                      disabled={
                        h.hostnameType === 'APP' && h.appId != null && disabledAppIds.has(h.appId)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {h.dnsVerified ? (
                      <Typography variant="caption" sx={{ color: 'success.main' }}>
                        {h.dnsVerifiedAt
                          ? t('hostnames.verifiedOn', 'Verified {{date}}', {
                              date: fmtDate(h.dnsVerifiedAt),
                            })
                          : t('hostnames.verified', 'DNS verified')}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: 'warning.main' }}>
                        {t('tenants.overview.hostnames.dnsPending', 'Pending')}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function SupportAccessAction({
  tenantId,
  hostnames,
}: {
  tenantId: number;
  hostnames: HostnameDto[] | null;
}) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const { i18n } = useTranslation();
  const locale =
    normalizeLocale(i18n.resolvedLanguage ?? i18n.language) ?? DEFAULT_LOCALE;
  const eligibleApps = (hostnames ?? []).filter(
    (h): h is HostnameDto & { appId: number } => h.hostnameType === 'APP' && h.appId != null,
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedHostnameId, setSelectedHostnameId] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [opening, setOpening] = useState(false);

  const selectedApp = eligibleApps.find((app) => app.id === selectedHostnameId) ?? eligibleApps[0];
  const noAppCopy = t('tenants.support.noApp', 'No app hostname configured for this tenant.');

  function appName(app: HostnameDto): string {
    return app.appName ?? app.appSlug ?? app.hostname;
  }

  function closePopover() {
    setAnchorEl(null);
    setReason('');
  }

  function showError(err: unknown) {
    if (err instanceof ApiError) {
      if (err.code === 'TENANT_INACTIVE') {
        snack.error(t('tenants.support.tenantInactive', 'This tenant is not active.'));
        return;
      }
      if (err.status === 404 && err.details.message) {
        snack.error(err.details.message);
        return;
      }
      if (err.status === 429) {
        snack.error(
          t(
            'errors.codes.RATE_LIMIT_REJECTED',
            'Too many support sessions opened. Try again in a minute.',
          ),
        );
        return;
      }
    }
    snack.error(t('errors.generic', 'Something went wrong.'));
  }

  async function handleOpen() {
    if (!selectedApp) return;

    const supportWindow = window.open('about:blank', '_blank');
    if (!supportWindow) {
      snack.error(t('tenants.support.popupBlocked', 'Allow pop-ups and try again.'));
      return;
    }
    supportWindow.opener = null;

    setOpening(true);
    try {
      const trimmedReason = reason.trim();
      const res = await apiAdmin.supportSessions.mint(tenantId, {
        appId: selectedApp.appId,
        ...(trimmedReason ? { reason: trimmedReason } : {}),
      });
      supportWindow.location.href = res.landingUrl;
      snack.success(
        t('tenants.support.opened', 'Support session open until {{until}}.', {
          until: fmtDateTime(res.expiresAt, locale),
        }),
      );
      closePopover();
    } catch (err) {
      supportWindow.close();
      showError(err);
    } finally {
      setOpening(false);
    }
  }

  return (
    <>
      <Stack spacing={0.25} sx={{ alignItems: 'flex-end' }}>
        <Button
          size="small"
          variant="outlined"
          disabled={eligibleApps.length === 0}
          onClick={(event) => {
            const initial = eligibleApps.find((app) => app.isPrimary) ?? eligibleApps[0];
            setSelectedHostnameId(initial?.id ?? '');
            setAnchorEl(event.currentTarget);
          }}
        >
          {t('tenants.support.action', 'Open as support')}
        </Button>
        {eligibleApps.length === 0 ? (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {noAppCopy}
          </Typography>
        ) : null}
      </Stack>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={opening ? undefined : closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack spacing={2} sx={{ p: 2, width: 360, maxWidth: 'calc(100vw - 32px)' }}>
          <Box>
            <Typography variant="subtitle1">
              {t('tenants.support.title', 'Open as support')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t(
                'tenants.support.helper',
                "Enter this tenant's app as platform support — you remain yourself, with platform access; this is audited.",
              )}
            </Typography>
          </Box>
          {eligibleApps.length > 1 ? (
            <FormControl size="small" fullWidth>
              <InputLabel id="support-app-label">{t('tenants.support.app', 'App')}</InputLabel>
              <Select
                labelId="support-app-label"
                label={t('tenants.support.app', 'App')}
                value={selectedHostnameId}
                onChange={(event) => setSelectedHostnameId(Number(event.target.value))}
              >
                {eligibleApps.map((app) => (
                  <MenuItem key={app.id} value={app.id}>
                    <Stack>
                      <Typography variant="body2">{appName(app)}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {app.hostname}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : selectedApp ? (
            <Box>
              <Typography variant="body2">{appName(selectedApp)}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {selectedApp.hostname}
              </Typography>
            </Box>
          ) : null}
          <TextField
            label={t('tenants.support.reason', 'Reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            slotProps={{ htmlInput: { maxLength: 500 } }}
            size="small"
            fullWidth
          />
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button onClick={closePopover} disabled={opening}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button variant="contained" onClick={() => void handleOpen()} disabled={opening}>
              {t('tenants.support.open', 'Open')}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}

// SASADM-0144 §3 — every role except USER, sorted OWNER → SUPER_ADMIN → ADMIN.
function OwnersCard({
  members,
  tenantId,
}: {
  members: TenantMemberDto[] | null;
  tenantId: number;
}) {
  const { t } = useCopy();
  const { pageRows, page, pageCount, setPage } = usePaged(members);

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
        >
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            {t('tenants.overview.cards.owners', 'Owners & Admins')}
          </Typography>
          <Link component={RouterLink} to={`/tenants/${tenantId}/members`} variant="body2">
            {t('tenants.overview.hostnames.manage', 'Manage')}
          </Link>
        </Stack>
        <Box sx={{ flexGrow: 1 }}>
          {members === null ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('common.loading', 'Loading')}
            </Typography>
          ) : members.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('tenants.overview.owners.empty', 'No owners or admins yet.')}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('tenants.fields.name', 'Name')}</TableCell>
                  <TableCell>{t('members.email', 'Email')}</TableCell>
                  <TableCell>{t('members.role', 'Role')}</TableCell>
                  <TableCell>{t('members.status', 'Status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(pageRows ?? []).map((m) => (
                  <TableRow key={m.membershipId}>
                    <TableCell>{m.name ?? m.email}</TableCell>
                    <TableCell>{m.email}</TableCell>
                    <TableCell>
                      <Chip size="small" label={t(`members.roles.${m.role}`, m.role)} />
                    </TableCell>
                    <TableCell>
                      <StatusChip kind="memberStatus" value={m.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
        {pageCount > 1 && (
          <Stack sx={{ alignItems: 'center', pt: 1 }}>
            <Pagination size="small" count={pageCount} page={page} onChange={(_e, p) => setPage(p)} />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

// SASADM-0144 §4 — pending invitations only; accepted/revoked/expired are history.
function InvitedCard({
  invitations,
  tenantId,
}: {
  invitations: TenantInvitationDto[] | null;
  tenantId: number;
}) {
  const { t } = useCopy();
  const { pageRows, page, pageCount, setPage } = usePaged(invitations);

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
        >
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            {t('tenants.overview.cards.invited', 'Invited')}
          </Typography>
          <Link component={RouterLink} to={`/tenants/${tenantId}/invitations`} variant="body2">
            {t('tenants.overview.hostnames.manage', 'Manage')}
          </Link>
        </Stack>
        <Box sx={{ flexGrow: 1 }}>
          {invitations === null ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('common.loading', 'Loading')}
            </Typography>
          ) : invitations.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('tenants.overview.invited.empty', 'No pending invitations.')}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('members.email', 'Email')}</TableCell>
                  <TableCell>{t('members.role', 'Role')}</TableCell>
                  <TableCell>{t('invitations.columns.expires', 'Expires')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(pageRows ?? []).map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>{inv.email}</TableCell>
                    <TableCell>
                      <Chip size="small" label={t(`members.roles.${inv.role}`, inv.role)} />
                    </TableCell>
                    <TableCell>{fmtDate(inv.expiresAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
        {pageCount > 1 && (
          <Stack sx={{ alignItems: 'center', pt: 1 }}>
            <Pagination size="small" count={pageCount} page={page} onChange={(_e, p) => setPage(p)} />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
